import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Route guard.
 *   <RequireAuth role="client">  - only authenticated clients (or admins)
 *   <RequireAuth role="admin">   - only admins
 *   <RequireAuth>                - any authenticated user
 */
export const RequireAuth = ({ role, children, allowForcedRotation = false }) => {
    const { user, isLoading, mustChangePassword } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return (
            <div
                className="flex min-h-[60vh] items-center justify-center bg-brand-base"
                data-testid="auth-loading"
            >
                <span className="font-display text-xs uppercase tracking-[0.2em] text-brand-muted">
                    Authenticating…
                </span>
            </div>
        );
    }

    if (!user) {
        const next = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/login?next=${next}`} replace />;
    }

    if (role) {
        // admin can access anything; any other role falls back to the public
        // home page (the client lease dashboard was retired).
        if (user.role !== 'admin' && user.role !== role) {
            return <Navigate to={user.role === 'admin' ? '/admin' : '/'} replace />;
        }
    }

    // Phase 1 forced-rotation gate: user must land on /admin/change-password
    // (where `allowForcedRotation` is true) before any other admin route is
    // reachable. Prevents a staffer with a leaked temp password from doing
    // anything but setting a new one.
    if (mustChangePassword && !allowForcedRotation) {
        return <Navigate to="/admin/change-password" replace />;
    }

    return children;
};

export default RequireAuth;
